import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  ChevronDown,
  LogOut,
  Search,
  Settings,
  ShieldCheck,
} from "lucide-react";

const navItems = [
  { label: "Today", href: "/dashboard" },
  { label: "Set Library", href: "/sets" },
  { label: "Inventory", href: "/inventory" },
  { label: "Intake", href: "/intake" },
];

export default function DashboardHeader({
  user,
  onLogout,
  activePage = "Today",
}) {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const initials = user.name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  useEffect(() => {
    const closeOnOutside = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  return (
    <header className="app-header" data-testid="dashboard-header">
      <Link className="brand" to="/dashboard" data-testid="brand-home-link">
        <span className="brand-mark" aria-hidden="true">
          C
        </span>
        <span>cardfolio</span>
      </Link>
      <nav
        className="main-nav"
        aria-label="Main navigation"
        data-testid="dashboard-navigation"
      >
        {navItems.map((item) => (
          <Link
            className={`nav-item ${item.label === activePage ? "is-active" : ""}`}
            to={item.href}
            data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}-link`}
            key={item.label}
          >
            {item.label}
          </Link>
        ))}
        {["owner", "admin"].includes(user.role) && (
          <Link
            className={`nav-item ${activePage === "Catalog" ? "is-active" : ""}`}
            to="/admin/catalog"
            data-testid="nav-catalog-link"
          >
            Catalog
          </Link>
        )}
        {["owner", "admin"].includes(user.role) && (
          <Link
            className={`nav-item ${activePage === "Admin" ? "is-active" : ""}`}
            to="/admin"
            data-testid="nav-admin-link"
          >
            Admin
          </Link>
        )}
      </nav>
      <div className="header-actions">
        <button
          className="icon-button search-button"
          type="button"
          aria-label="Search inventory"
          data-testid="inventory-search-button"
        >
          <Search size={18} strokeWidth={1.7} />
        </button>
        <button
          className="icon-button"
          type="button"
          aria-label="View notifications"
          data-testid="notifications-button"
        >
          <Bell size={18} strokeWidth={1.7} />
          <span className="notification-dot" />
        </button>
        <div className="account-menu" ref={accountMenuRef}>
          <button
            aria-controls="account-menu-popover"
            aria-expanded={accountMenuOpen}
            className="account-button"
            onClick={() => setAccountMenuOpen((current) => !current)}
            type="button"
            data-testid="account-menu-trigger"
          >
            <span className="avatar" data-testid="account-menu-avatar">
              {initials}
            </span>
            <span className="account-name" data-testid="account-menu-name">
              {user.name}
            </span>
            <ChevronDown
              data-testid="account-menu-chevron"
              size={15}
              strokeWidth={1.7}
            />
          </button>
          {accountMenuOpen && (
            <div
              className="account-menu-popover"
              id="account-menu-popover"
              data-testid="account-menu-popover"
            >
              <p data-testid="account-menu-user-email">{user.email}</p>
              <Link
                to="/admin"
                onClick={() => setAccountMenuOpen(false)}
                data-testid="account-menu-organization-settings"
              >
                <Settings size={15} /> Organization settings
              </Link>
              {user.is_site_admin === true && (
                <Link
                  to="/site-admin/import-review"
                  onClick={() => setAccountMenuOpen(false)}
                  data-testid="account-menu-site-admin"
                >
                  <ShieldCheck size={15} /> Site administration
                </Link>
              )}
              <button
                onClick={() => {
                  setAccountMenuOpen(false);
                  onLogout();
                }}
                type="button"
                data-testid="account-menu-logout"
              >
                <LogOut size={15} /> Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
